import useTranslation from "../../client/hooks/useTranslation";

/**
 * Hero visual (Homepage V2) — a faithful HTML/CSS reconstruction of the Edgely
 * dashboard (NOT a screenshot, NOT scattered cards). Because it's real DOM, it
 * stays razor-sharp at any zoom/resolution and weighs almost nothing.
 *
 * Motion strategy (this is why earlier versions stuttered):
 *   - The whole mockup floats as ONE block via a pure-CSS @keyframes
 *     (`.hero-mockup-float`, translate3d → GPU-composited, off main thread).
 *   - The 3D tilt is a STATIC transform on an inner element, so perspective is
 *     not recomputed each frame.
 *   - No framer-motion, no animated backdrop-blur (the real jank culprit:
 *     blurred layers re-rasterize every frame while moving).
 *
 * The landing is locked to Classic Dark, so colors use the dark palette
 * directly. All figures are decorative demo data.
 *
 * Responsive: below lg the tilt + float are dropped and the frame renders flat
 * (still the same reconstructed dashboard, just calmer and fully legible).
 */

const TEAL = "#00C9A7";
const EMERALD = "#34D399";
const RED = "#FF5252";
const PANEL = "#1E293B";
const BG = "#0F1729";

/* Module header used by every panel: ● teal dot + mono uppercase label + rule. */
function PanelHeader({ label, right }: { label: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-1.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TEAL }} />
        <span className="truncate font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-white/55">
          {label}
        </span>
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

function Panel({
  label,
  className,
  children,
  right,
  fill,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
  /** Grow to fill its flex parent's height (body stretches) — used to remove
      voids when two columns of unequal natural height sit side by side. */
  fill?: boolean;
}) {
  return (
    <div
      className={`flex flex-col overflow-hidden rounded-lg border border-white/10 ${
        fill ? "h-full" : ""
      } ${className ?? ""}`}
      style={{ backgroundColor: PANEL }}
    >
      <PanelHeader label={label} right={right} />
      <div className={`px-3 py-2.5 ${fill ? "flex-1" : ""}`}>{children}</div>
    </div>
  );
}

/* ── KPI panels ─────────────────────────────────────────────────────────── */

function KpiNetPnl({ label, tradesLabel }: { label: string; tradesLabel: string }) {
  return (
    <Panel label={label}>
      <p className="font-mono text-xl font-bold leading-none" style={{ color: EMERALD }}>
        +$1,499
      </p>
      <p className="mt-1 font-mono text-[9px] text-white/40">108 {tradesLabel}</p>
    </Panel>
  );
}

function KpiProfitFactor({ label }: { label: string }) {
  return (
    <Panel label={label}>
      <p className="font-mono text-xl font-bold leading-none text-white">1.54</p>
      <p className="mt-1 font-mono text-[9px] text-white/40">$53.2 / $98.4</p>
    </Panel>
  );
}

function KpiWinRate({ label }: { label: string }) {
  return (
    <Panel label={label}>
      <p className="font-mono text-xl font-bold leading-none text-white">74.1%</p>
      <p className="mt-1 font-mono text-[9px]">
        <span style={{ color: EMERALD }}>80W</span>
        <span className="text-white/30"> · 0B · </span>
        <span style={{ color: RED }}>28L</span>
      </p>
    </Panel>
  );
}

function KpiAvgWinLoss({ label, avgWin, avgLoss }: { label: string; avgWin: string; avgLoss: string }) {
  return (
    <Panel label={label}>
      <div className="space-y-1.5">
        {[
          { name: avgWin, val: "+$53.20", color: EMERALD, w: "38%" },
          { name: avgLoss, val: "$98.45", color: RED, w: "70%" },
        ].map((r) => (
          <div key={r.name}>
            <div className="flex items-center justify-between font-mono text-[9px]">
              <span className="text-white/50">{r.name}</span>
              <span style={{ color: r.color }}>{r.val}</span>
            </div>
            <div className="mt-0.5 h-[3px] w-full overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full" style={{ width: r.w, backgroundColor: r.color }} />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

const MARKETS = [
  { name: "NYSE", time: "16:38" },
  { name: "LSE", time: "21:38" },
  { name: "Tokyo", time: "05:38" },
  { name: "Euronext", time: "22:38" },
];

function MarketHours({ label, closed }: { label: string; closed: string }) {
  return (
    <Panel label={label}>
      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
        {MARKETS.map((m) => (
          <div key={m.name} className="flex min-w-0 items-center gap-1">
            <span className="h-1 w-1 shrink-0 rounded-full bg-white/25" />
            <span className="truncate font-mono text-[9px] font-semibold text-white">{m.name}</span>
            {/* L'heure saute sous sm : la place manque et le nom du marché prime. */}
            <span className="hidden truncate font-mono text-[8px] text-white/30 sm:inline">{closed}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ── Calendar (large) ───────────────────────────────────────────────────── */

type Day = { n: number; pnl?: number; trades?: number; account?: string };
type Week = { days: Day[]; total?: number; trades?: number };

// Mon–Fri grid + weekly recap column, mirroring the real dashboard.
const CAL_WEEKS: Week[] = [
  { days: [{ n: 1 }, { n: 2 }, { n: 3 }, { n: 4 }, { n: 5 }] },
  {
    days: [
      { n: 8 },
      { n: 9 },
      { n: 10, pnl: 310.5, trades: 9, account: "Lucid" },
      { n: 11, pnl: 111, trades: 18, account: "Lucid" },
      { n: 12, pnl: 223, trades: 8, account: "Lucid" },
    ],
    total: 644.5,
    trades: 35,
  },
  {
    days: [
      { n: 15, pnl: 140, trades: 9, account: "Lucid" },
      { n: 16, pnl: -407.5, trades: 28, account: "Lucid" },
      { n: 17, pnl: 654, trades: 19, account: "Lucid" },
      { n: 18, pnl: 468.5, trades: 17, account: "Lucid" },
      { n: 19 },
    ],
    total: 855,
    trades: 73,
  },
  { days: [{ n: 22 }, { n: 23 }, { n: 24 }, { n: 25 }, { n: 26 }] },
];

const DOW = ["lun.", "mar.", "mer.", "jeu.", "ven."];

function fmt(v: number) {
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}$`;
}

function CalendarDay({ d }: { d: Day }) {
  const has = d.pnl !== undefined;
  const win = (d.pnl ?? 0) >= 0;
  return (
    <div
      className="flex h-full min-h-[44px] flex-col justify-between rounded-[4px] p-1"
      style={{
        backgroundColor: has
          ? win
            ? "rgba(52,211,153,0.10)"
            : "rgba(255,82,82,0.10)"
          : "rgba(255,255,255,0.025)",
        border: `1px solid ${
          has ? (win ? "rgba(52,211,153,0.4)" : "rgba(255,82,82,0.4)") : "rgba(255,255,255,0.05)"
        }`,
      }}
    >
      <span className="font-mono text-[8px] text-white/40">{d.n}</span>
      {has ? (
        // `min-w-0` + `truncate` : la cellule est plus étroite que le montant sur
        // les petits écrans — on tronque proprement au lieu de laisser le texte
        // déborder sur la cellule voisine.
        <div className="min-w-0 leading-tight">
          <span
            className="block truncate font-mono text-[7px] font-bold sm:text-[8.5px]"
            style={{ color: win ? EMERALD : RED }}
          >
            {fmt(d.pnl as number)}
          </span>
          {d.account && (
            <span
              className="mt-0.5 hidden max-w-full truncate rounded-[2px] px-1 font-mono text-[6px] text-white/50 sm:inline-block"
              style={{ backgroundColor: "rgba(255,255,255,0.06)" }}
            >
              {d.account}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

function WeekRecap({ w }: { w: Week }) {
  if (w.total === undefined) {
    return <div className="flex items-center justify-center text-[8px] text-white/20">—</div>;
  }
  const win = w.total >= 0;
  return (
    <div className="flex flex-col items-center justify-center">
      <span className="font-mono text-[9px] font-bold" style={{ color: win ? EMERALD : RED }}>
        {fmt(w.total)}
      </span>
      <span className="font-mono text-[7px] text-white/35">{w.trades} trades</span>
    </div>
  );
}

function CalendarPanel({
  label,
  weekLabel,
  fill,
}: {
  label: string;
  weekLabel: string;
  fill?: boolean;
}) {
  return (
    <Panel label={label} fill={fill}>
      {/* header row: weekday names + Sem. */}
      <div className="mb-1 grid grid-cols-6 gap-1">
        {DOW.map((d) => (
          <span key={d} className="font-mono text-[7.5px] text-white/35">
            {d}
          </span>
        ))}
        <span className="text-right font-mono text-[7.5px] text-white/35">{weekLabel}</span>
      </div>
      <div className={`flex flex-col gap-1 ${fill ? "h-full" : ""}`}>
        {CAL_WEEKS.map((w, i) => (
          <div key={i} className={`grid grid-cols-6 gap-1 ${fill ? "flex-1" : ""}`}>
            {w.days.map((d) => (
              <CalendarDay key={d.n} d={d} />
            ))}
            <WeekRecap w={w} />
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ── Economic calendar / routines / goals / news ────────────────────────── */

const ECO = [
  { time: "08:00", event: "German PPI MoM", val: "0.3%", up: false },
  { time: "08:00", event: "UK Retail Sales YoY", val: "3.2%", up: true },
  { time: "08:00", event: "UK Core Retail MoM", val: "1.2%", up: true },
  { time: "08:00", event: "German PPI YoY", val: "2.2%", up: true },
  { time: "09:10", event: "ECB's Lane Speaks", val: "—", up: true },
];

function EcoCalendar({ label }: { label: string }) {
  return (
    <Panel label={label}>
      <div className="space-y-1">
        {ECO.map((e, i) => (
          <div key={i} className="flex items-center gap-1.5 font-mono text-[9px]">
            <span className="text-white/30">{e.time}</span>
            <span className="flex-1 truncate text-white/65">{e.event}</span>
            <span style={{ color: e.val === "—" ? "rgba(255,255,255,0.3)" : e.up ? EMERALD : RED }}>
              {e.val}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Routines({
  label,
  preOpen,
  openUs,
  daily,
}: {
  label: string;
  preOpen: string;
  openUs: string;
  daily: string;
}) {
  return (
    <Panel label={label}>
      <div className="space-y-1.5">
        {[preOpen, openUs].map((name) => (
          <div key={name} className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TEAL }} />
            <span className="flex-1 truncate text-[10px] text-white/80">{name}</span>
            <span className="rounded-full bg-white/[0.06] px-1.5 py-px font-mono text-[7px] uppercase tracking-wide text-white/40">
              {daily}
            </span>
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: TEAL }} />
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Goals({ label, goalName }: { label: string; goalName: string }) {
  return (
    <Panel label={label}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-white/80">{goalName}</span>
        <span className="font-mono text-[9px] text-white/40">0%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full" style={{ width: "8%", backgroundColor: TEAL }} />
      </div>
    </Panel>
  );
}

const NEWS = [
  { time: "22:15", text: "US bank deposits rose to $19.354 tln" },
  { time: "22:11", text: "Trump ends remarks at Joint Base Andrews" },
  { time: "21:52", text: "China's President Xi coming to the US" },
];

function News({ label }: { label: string }) {
  return (
    <Panel label={label}>
      <div className="space-y-1">
        {NEWS.map((n) => (
          <div key={n.time} className="flex items-start gap-1.5 text-[9px] leading-snug">
            <span className="mt-px font-mono text-white/30">{n.time}</span>
            <span className="flex-1 truncate text-white/60">{n.text}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ── Window chrome (app frame top bar) ──────────────────────────────────── */

function WindowChrome({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2">
      <div className="flex gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      </div>
      <span className="font-display text-[13px] font-bold text-white">{title}</span>
    </div>
  );
}

/* ── Assembled mockup (the aligned dashboard) ───────────────────────────── */

function DashboardMockup() {
  const t = useTranslation();
  const f = t.marketing.floatingDashboard;
  return (
    <div
      className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
      style={{ backgroundColor: BG, boxShadow: "0 40px 90px rgba(0,0,0,0.55)" }}
    >
      <WindowChrome title={f.dashboardTitle} />

      <div className="space-y-1.5 p-2.5">
        {/* KPI row — 2 colonnes sur mobile : à 5 de front sous ~400px, chaque
            tuile tombe à ~65px et les montants mono débordent les unes sur les
            autres. Les 5 tuiles ne reviennent qu'à partir de sm. */}
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
          <KpiNetPnl label={f.netPnlLabel} tradesLabel={f.tradesLabel} />
          <KpiProfitFactor label={f.profitFactorLabel} />
          <KpiWinRate label={f.winRateLabel} />
          <KpiAvgWinLoss label={f.avgWinLossLabel} avgWin={f.avgWin} avgLoss={f.avgLoss} />
          <MarketHours label={f.marketHoursLabel} closed={f.closed} />
        </div>

        {/* Main grid (mirrors the dashboard): calendar (left, wide, with Sem.
            column) + right column stacking Eco calendar → Routines → Goals →
            News. items-stretch keeps both columns equal height; the right
            column flexes so its panels fill the calendar's height with no gap. */}
        {/* items-stretch makes both columns share the (taller) right-column
            height; the calendar fills it (week rows grow) so there's no void
            in its lower half. */}
        {/* Sous sm, le calendrier passe pleine largeur et la colonne de droite
            se place dessous : à 3/5 de ~370px il ne restait ~35px par jour, soit
            moins que la largeur d'un montant (`+310.50$`), d'où les
            chevauchements. */}
        <div className="grid grid-cols-1 items-stretch gap-1.5 sm:grid-cols-5">
          <div className="flex flex-col sm:col-span-3">
            <CalendarPanel label={f.calendarLabel} weekLabel={f.weekLabel} fill />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <EcoCalendar label={f.ecoCalendarLabel} />
            <Routines
              label={f.routinesLabel}
              preOpen={f.routinePreOpen}
              openUs={f.routineOpenUs}
              daily={f.daily}
            />
            <Goals label={f.goalsLabel} goalName={f.goalName} />
          </div>
        </div>

        {/* Bottom row: news (full width) */}
        <News label={f.newsLabel} />
      </div>
    </div>
  );
}

export default function FloatingDashboard() {
  const t = useTranslation();
  const f = t.marketing.floatingDashboard;

  return (
    <div role="img" aria-label={f.ariaLabel} className="w-full">
      {/* lg+ : floating block + static 3D tilt. The float animates ONE layer;
          the tilt below it is static, so perspective never recomputes. */}
      <div className="hidden lg:block" style={{ perspective: "1800px" }}>
        <div className="hero-mockup-float">
          <div
            style={{
              transform: "rotateX(6deg) rotateY(-9deg) rotate(1deg)",
              transformStyle: "preserve-3d",
            }}
          >
            <DashboardMockup />
          </div>
        </div>
      </div>

      {/* < lg : flat, no tilt/float — calm and fully legible */}
      <div className="lg:hidden">
        <DashboardMockup />
      </div>
    </div>
  );
}
